import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  User,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

import PageHeader from '../../components/layout/PageHeader';
import { accessRequestService } from '../../services/accessRequest.service';
import type { AccessRequest } from '../../types/accessRequest.types';
import { formatDocumentDisplayName } from '../../utils/documentName';
import { ROUTES } from '../../config/routes.config';

type StatusTab = 'Pending' | 'Approved' | 'Denied';

const TAB_CONFIG: Record<
  StatusTab,
  {
    icon: React.ReactNode;
    color: string;
    bg: string;
    emptyMessage: string;
  }
> = {
  Pending: {
    icon: <Clock size={14} />,
    color: 'var(--color-warning)',
    bg: 'var(--color-warning-light)',
    emptyMessage:
      'No pending access requests. Requests you submit will appear here until reviewed.',
  },
  Approved: {
    icon: <CheckCircle2 size={14} />,
    color: 'var(--color-success)',
    bg: 'var(--color-success-light)',
    emptyMessage: 'No approved requests yet.',
  },
  Denied: {
    icon: <XCircle size={14} />,
    color: 'var(--color-error)',
    bg: 'var(--color-error-light)',
    emptyMessage: 'No denied requests.',
  },
};

const StatusBadge: React.FC<{ status: StatusTab }> = ({ status }) => {
  const cfg = TAB_CONFIG[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: cfg.bg,
        color: cfg.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
      }}
    >
      {cfg.icon} {status}
    </span>
  );
};

const DeniedRow: React.FC<{
  item: AccessRequest;
  isOdd: boolean;
}> = ({ item, isOdd }) => {
  const [open, setOpen] = useState(false);
  const rowBg = isOdd ? 'var(--color-gray-50)' : 'white';

  return (
    <>
      <tr
        style={{
          borderBottom: '1px solid var(--color-gray-100)',
          background: rowBg,
        }}
      >
        <td style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            <FileText
              size={14}
              style={{
                color: 'var(--color-gray-400)',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {formatDocumentDisplayName({
                cmsNumber: item.cms_number,
                originalFileName: item.original_file_name,
                documentType: item.document_type,
              })}
            </span>
          </div>
        </td>
        <td
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-gray-600)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          {item.cms_number}
        </td>
        <td style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-1)',
            }}
          >
            <User size={12} style={{ color: 'var(--color-gray-400)' }} />
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-gray-600)',
              }}
            >
              {item.requested_to_name ?? `User ID ${item.requested_to_user_id}`}
            </span>
          </div>
        </td>
        <td
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            maxWidth: 200,
          }}
        >
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-gray-600)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.reason}
          </p>
        </td>
        <td style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
          <StatusBadge status="Denied" />
        </td>
        <td
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-gray-500)',
          }}
        >
          {item.decision_at
            ? format(new Date(item.decision_at), 'dd MMM yyyy')
            : '—'}
        </td>
        <td style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: 'var(--color-error-light)',
              border: 'none',
              color: 'var(--color-error)',
              padding: '3px 8px',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {open ? (
              <ChevronUp size={12} />
            ) : (
              <ChevronDown size={12} />
            )}
            {open ? 'Hide' : 'Reason'}
          </button>
        </td>
      </tr>
      {open && (
        <tr style={{ background: 'var(--color-error-light)' }}>
          <td
            colSpan={7}
            style={{
              padding:
                'var(--spacing-3) var(--spacing-4) var(--spacing-4) var(--spacing-10)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-2)',
              }}
            >
              <AlertCircle
                size={14}
                style={{
                  color: 'var(--color-error)',
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />
              <div>
                <p
                  style={{
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-error)',
                    fontSize: 'var(--font-size-sm)',
                    marginBottom: '4px',
                  }}
                >
                  Reason for Denial
                </p>
                <p
                  style={{
                    color: 'var(--color-error)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {item.decision_reason || 'No reason provided'}
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

function mapRowToAccessRequest(row: any): AccessRequest {
  const doc = row.document ?? {};
  const caseData = row.case ?? {};
  return {
    request_id: row.request_id,
    document_id: row.document_id,
    case_id: caseData.case_id ? String(caseData.case_id) : undefined,
    original_file_name: doc.original_file_name ?? '',
    // File No (new form) falls back to the legacy CMS number for old records.
    cms_number: caseData.fir_number || caseData.cms_number || '',
    rrms_ref_number: caseData.rrms_ref_number ?? '',
    case_type: caseData.category || caseData.case_type || '',
    folder_type: doc.folder_type ?? '',
    document_type: doc.document_type ?? '',
    classification: 'Confidential',
    requested_to_user_id: row.content_manager_user_id ?? 0,
    requested_to_name: (row as any).requested_to_name,
    reason: row.reason ?? '',
    status: row.status === 'Rejected' ? 'Denied' : row.status,
    decision_reason: row.decision_reason,
    created_at: row.created_at,
    decision_at: row.decided_at,
  };
}

const AccessStatusPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<StatusTab>('Pending');
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['access-status', activeTab, page],
    queryFn: () =>
      accessRequestService.getAccessStatus({
        status: activeTab,
        page,
        limit: 15,
      }),
    select: (res) => {
      const raw = res.data?.data ?? res.data;
      const items = (raw?.items ?? []).map(mapRowToAccessRequest);
      return { items, pagination: raw?.pagination };
    },
  });

  const items: AccessRequest[] = data?.items ?? [];
  const pagination = data?.pagination;
  const tabConfig = TAB_CONFIG[activeTab];
  const tableHeaders = [
    'Document',
    'File No',
    'Requested To',
    'Reason',
    'Status',
    'Decision Date',
    'Actions',
  ];

  useEffect(() => {
    document.title = 'RRMS | Access Status';
  }, []);

  useEffect(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'pending') {
      setActiveTab('Pending');
      setPage(1);
    }
    if (tab === 'approved') {
      setActiveTab('Approved');
      setPage(1);
    }
    if (tab === 'denied' || tab === 'rejected') {
      setActiveTab('Denied');
      setPage(1);
    }
  }, [searchParams]);

  return (
    <div className="">
      <PageHeader
        title="Access Status"
        subtitle="Track the status of your access requests for confidential documents"
      />

      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 'var(--spacing-5)',
          borderBottom: '1px solid var(--color-gray-200)',
        }}
      >
        {(['Pending', 'Approved', 'Denied'] as StatusTab[]).map((tab) => {
          const cfg = TAB_CONFIG[tab];
          return (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveTab(tab);
                setPage(1);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-3) var(--spacing-5)',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight:
                  activeTab === tab
                    ? 'var(--font-weight-semibold)'
                    : 'var(--font-weight-regular)',
                color: activeTab === tab ? cfg.color : 'var(--color-gray-500)',
                borderBottom:
                  activeTab === tab
                    ? `2px solid ${cfg.color}`
                    : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'all var(--transition-fast)',
              }}
            >
              <span style={{ color: cfg.color }}>{cfg.icon}</span>
              {tab}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div className="card" style={{ overflow: 'hidden' }}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                padding: 'var(--spacing-4)',
                borderBottom: '1px solid var(--color-gray-100)',
                display: 'flex',
                gap: 'var(--spacing-3)',
              }}
            >
              {[...Array(6)].map((_, j) => (
                <div
                  key={j}
                  style={{
                    height: 14,
                    flex: j === 0 ? 2 : 1,
                    background: 'var(--color-gray-100)',
                    borderRadius: 4,
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div
          className="card"
          style={{
            padding: 'var(--spacing-10)',
            textAlign: 'center',
          }}
        >
          <AlertCircle
            size={36}
            style={{
              color: 'var(--color-error)',
              margin: '0 auto var(--spacing-3)',
            }}
          />
          <p
            style={{
              color: 'var(--color-error)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            Failed to load access status
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div
          className="card"
          style={{
            padding: 'var(--spacing-16)',
            textAlign: 'center',
          }}
        >
          {activeTab === 'Approved' ? (
            <CheckCircle2
              size={48}
              style={{
                color: 'var(--color-success)',
                margin: '0 auto var(--spacing-4)',
              }}
            />
          ) : activeTab === 'Denied' ? (
            <XCircle
              size={48}
              style={{
                color: 'var(--color-error)',
                margin: '0 auto var(--spacing-4)',
              }}
            />
          ) : (
            <Clock
              size={48}
              style={{
                color: 'var(--color-gray-300)',
                margin: '0 auto var(--spacing-4)',
              }}
            />
          )}
          <p
            style={{
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-gray-600)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            No {activeTab.toLowerCase()} requests
          </p>
          <p
            style={{
              color: 'var(--color-gray-400)',
              fontSize: 'var(--font-size-sm)',
              maxWidth: 400,
              margin: '0 auto',
            }}
          >
            {tabConfig.emptyMessage}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="card">
          <div className="table-responsive">
            <table
              style={{
                width: '100%',
                minWidth: 800,
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    background: 'var(--color-gray-50)',
                    borderBottom: '1px solid var(--color-gray-200)',
                  }}
                >
                  {tableHeaders.map((h, i) =>
                    activeTab !== 'Denied' &&
                    i === tableHeaders.length - 1 ? null : (
                      <th
                        key={h}
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          textAlign: 'left',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--color-gray-500)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) =>
                  activeTab === 'Denied' ? (
                    <DeniedRow
                      key={item.request_id}
                      item={item}
                      isOdd={idx % 2 !== 0}
                    />
                  ) : (
                    <tr
                      key={item.request_id}
                      style={{
                        borderBottom: '1px solid var(--color-gray-100)',
                        background:
                          idx % 2 === 0 ? 'white' : 'var(--color-gray-50)',
                      }}
                    >
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (!item.case_id) return;
                            navigate(
                              ROUTES.CASE_DETAIL.replace(
                                ':caseId',
                                String(item.case_id)
                              )
                            );
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-2)',
                            padding: 0,
                            margin: 0,
                            border: 'none',
                            background: 'none',
                            cursor: item.case_id ? 'pointer' : 'default',
                            textAlign: 'left',
                          }}
                        >
                          <FileText
                            size={14}
                            style={{
                              color: 'var(--color-gray-400)',
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 'var(--font-weight-medium)',
                              textDecoration: item.case_id
                                ? 'underline'
                                : 'none',
                              color: item.case_id
                                ? 'var(--color-primary)'
                                : 'var(--color-gray-700)',
                            }}
                          >
                            {formatDocumentDisplayName({
                              cmsNumber: item.cms_number,
                              originalFileName: item.original_file_name,
                              documentType: item.document_type,
                            })}
                          </span>
                        </button>
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-gray-600)',
                          fontFamily: 'var(--font-family-mono)',
                        }}
                      >
                        {item.cms_number}
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-1)',
                          }}
                        >
                          <User
                            size={12}
                            style={{ color: 'var(--color-gray-400)' }}
                          />
                          <span
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-gray-600)',
                            }}
                          >
                            {item.requested_to_name ??
                              `User ID ${item.requested_to_user_id}`}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          maxWidth: 200,
                        }}
                      >
                        <p
                          style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-gray-600)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.reason}
                        </p>
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                        }}
                      >
                        <StatusBadge status={activeTab} />
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-gray-500)',
                        }}
                      >
                        {activeTab === 'Pending'
                          ? `Submitted ${formatDistanceToNow(
                              new Date(item.created_at),
                              { addSuffix: true }
                            )}`
                          : item.decision_at
                          ? format(
                              new Date(item.decision_at),
                              'dd MMM yyyy'
                            )
                          : '—'}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div
              style={{
                padding: 'var(--spacing-4)',
                borderTop: '1px solid var(--color-gray-200)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-gray-500)',
                }}
              >
                Showing {(page - 1) * 15 + 1}–
                {Math.min(page * 15, pagination.total)} of {pagination.total}{' '}
                requests
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-1)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-3)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--radius-md)',
                    background: 'white',
                    cursor: pagination.hasPrevPage
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: pagination.hasPrevPage ? 1 : 0.4,
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  Previous
                </button>
                <span
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-3)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-gray-600)',
                  }}
                >
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(pagination.totalPages, p + 1))
                  }
                  disabled={!pagination.hasNextPage}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-3)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--radius-md)',
                    background: 'white',
                    cursor: pagination.hasNextPage
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: pagination.hasNextPage ? 1 : 0.4,
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccessStatusPage;
